import { useState, useMemo, useRef } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency } from '../../lib/formatters'
import { calculateSavingsGoal } from '../../lib/calculations/savingsGoal'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'

function InputField({ label, value, onChange, prefix, suffix, type = 'number', hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

const GOAL_PRESETS = [
  { label: 'Emergency Fund', amount: 20000 },
  { label: 'Vacation', amount: 5000 },
  { label: 'Down Payment', amount: 60000 },
  { label: 'New Car', amount: 30000 },
  { label: 'Wedding', amount: 35000 },
]

export default function SavingsGoal() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)

  const [goalAmount, setGoalAmount] = useState(20000)
  const [currentSavings, setCurrentSavings] = useState(3000)
  const [targetDate, setTargetDate] = useState(() => {
    const d = new Date()
    d.setFullYear(d.getFullYear() + 2)
    return d.toISOString().slice(0, 10)
  })
  const [expectedReturn, setExpectedReturn] = useState(4.5)

  const result = useMemo(() => calculateSavingsGoal({
    goalAmount, currentSavings, targetDate, expectedReturn,
  }), [goalAmount, currentSavings, targetDate, expectedReturn])

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14

    const ensureSpace = (needed) => {
      if ((doc.lastAutoTable?.finalY || margin) + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return (doc.lastAutoTable?.finalY || margin) + 10
    }

    const addSectionTitle = (title, y) => {
      doc.setFontSize(13)
      doc.setTextColor(40, 40, 40)
      doc.text(title, margin, y)
      return y + 6
    }

    // ── Header ──
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, pageHeight, 'F')
    doc.setFontSize(22)
    doc.setTextColor(30, 30, 30)
    doc.text('Savings Goal Report', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(128, 128, 128)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)
    doc.setDrawColor(220, 220, 220)
    doc.line(margin, 33, pageWidth - margin, 33)

    // ── Section 1: Goal Preset ──
    const matchedPreset = GOAL_PRESETS.find(p => p.amount === goalAmount)
    let y = 40
    if (matchedPreset) {
      doc.setFontSize(10)
      doc.setTextColor(80, 80, 80)
      doc.text(`Selected Preset: ${matchedPreset.label}`, margin, y)
      y += 8
    }

    // ── Section 2: Goal Details (Inputs) ──
    y = addSectionTitle('Goal Details', y)

    autoTable(doc, {
      head: [['Input', 'Value']],
      body: [
        ['Goal Amount', formatCurrency(goalAmount)],
        ['Current Savings', formatCurrency(currentSavings)],
        ['Target Date', new Date(targetDate).toLocaleDateString()],
        ['Expected Annual Return', `${expectedReturn}%`],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    })

    // ── Section 3: Key Results ──
    y = ensureSpace(60)
    y = addSectionTitle('Key Results', y)

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: [
        ['Monthly Savings Needed', formatCurrency(result.monthlyNeeded)],
        ['Weekly Savings Needed', formatCurrency(result.weeklyNeeded)],
        ['Total Months', `${result.totalMonths} months`],
        ['Total Contributions', formatCurrency(result.totalContributed)],
        ['Total Growth Earned', formatCurrency(result.totalGrowth)],
        ['Remaining Gap', formatCurrency(result.gap)],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    })

    // ── Section 4: Current Progress ──
    y = ensureSpace(40)
    y = addSectionTitle('Current Progress', y)

    autoTable(doc, {
      head: [['Detail', 'Value']],
      body: [
        ['Progress', `${result.progress}%`],
        ['Current Savings', formatCurrency(result.currentSavings)],
        ['Goal Amount', formatCurrency(result.goalAmount)],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    })

    // Draw a progress bar in the PDF
    y = (doc.lastAutoTable?.finalY || y) + 6
    if (y + 14 > pageHeight - margin) { doc.addPage(); y = margin + 4 }
    const barWidth = pageWidth - margin * 2
    const barHeight = 8
    doc.setFillColor(230, 230, 230)
    doc.roundedRect(margin, y, barWidth, barHeight, 3, 3, 'F')
    const fillWidth = Math.min(100, result.progress) / 100 * barWidth
    if (result.progress >= 100) {
      doc.setFillColor(34, 197, 94)
    } else {
      doc.setFillColor(59, 130, 246)
    }
    if (fillWidth > 0) doc.roundedRect(margin, y, fillWidth, barHeight, 3, 3, 'F')
    doc.setFontSize(8)
    doc.setTextColor(80, 80, 80)
    doc.text(`${result.progress}%`, margin + barWidth / 2 - 5, y + barHeight + 8)

    // ── Section 5: Savings Projection Table ──
    if (result.projection.length > 0) {
      y = y + barHeight + 16
      if (y + 60 > pageHeight - margin) { doc.addPage(); y = margin + 4 }
      y = addSectionTitle('Savings Projection Timeline', y)

      const projectionRows = result.projection.map(p => [
        `Month ${p.month}`,
        formatCurrency(p.balance),
        formatCurrency(p.contributions),
        formatCurrency(p.growth),
      ])

      autoTable(doc, {
        head: [['Month', 'Balance', 'Contributions', 'Growth']],
        body: projectionRows,
        startY: y,
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [107, 114, 128], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
      })
    }

    // ── Section 6: Chart Capture ──
    if (chartRef.current) {
      await addChartToPDF(doc, chartRef, { title: 'Savings Projection Chart' })
    }

    // ── Footer on every page ──
    const totalPages = doc.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFillColor(255, 255, 255)
      doc.setFontSize(8)
      doc.setTextColor(180, 180, 180)
      doc.text(`Liquid Finance  |  Savings Goal Report  |  Page ${i} of ${totalPages}`, margin, pageHeight - 8)
    }

    doc.save(`savings-goal-report-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        {GOAL_PRESETS.map(preset => (
          <button
            key={preset.label}
            onClick={() => setGoalAmount(preset.amount)}
            className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
              goalAmount === preset.amount ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
            }`}
            style={goalAmount === preset.amount ? {
              background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
              border: `1px solid ${colors.primary}44`,
            } : { border: '1px solid var(--card-border)' }}
          >
            {preset.label} ({formatCurrency(preset.amount)})
          </button>
        ))}
      </div>

      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-heading">Goal Details</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <InputField label="Goal Amount" value={goalAmount} onChange={setGoalAmount} prefix="$" />
          <InputField label="Current Savings" value={currentSavings} onChange={setCurrentSavings} prefix="$" />
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Target Date</label>
            <input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
              className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:border-primary transition"
            />
          </div>
          <InputField label="Expected Return" value={expectedReturn} onChange={setExpectedReturn} suffix="%" hint="HYSA or investment return" />
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard label="Monthly Savings Needed" value={formatCurrency(result.monthlyNeeded)} color={colors.primary} highlight />
        <ResultCard label="Weekly Savings" value={formatCurrency(result.weeklyNeeded)} color={colors.secondary} />
        <ResultCard label="Total Growth Earned" value={formatCurrency(result.totalGrowth)} color={colors.positive} />
        <ResultCard label="Progress" value={`${result.progress}%`} color={result.progress >= 100 ? colors.positive : colors.primary} />
      </div>

      {/* Progress Bar */}
      <GlassCard>
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-semibold text-heading">Current Progress</h3>
          <span className="text-sm font-medium" style={{ color: result.progress >= 100 ? colors.positive : colors.primary }}>
            {formatCurrency(result.currentSavings)} / {formatCurrency(result.goalAmount)}
          </span>
        </div>
        <div className="w-full h-4 rounded-full bg-subtle overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(100, result.progress)}%`,
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
            }}
          />
        </div>
      </GlassCard>

      {/* Projection Chart */}
      {result.projection.length > 1 && (
        <GlassCard>
          <h3 className="text-lg font-semibold text-heading mb-4">Savings Projection</h3>
          <div className="h-64" ref={chartRef}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={result.projection}>
                <defs>
                  <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.primary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={colors.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: colors.chartTick }} label={{ value: 'Months', position: 'insideBottom', offset: -5, style: { fontSize: 11, fill: colors.chartTick } }} />
                <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: colors.chartTick }} />
                <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={l => `Month ${l}`} contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 }} />
                <ReferenceLine y={result.goalAmount} stroke={colors.positive} strokeDasharray="5 5" label={{ value: 'Goal', fill: colors.positive, fontSize: 11 }} />
                <Area type="monotone" dataKey="balance" stroke={colors.primary} fill="url(#savingsGrad)" strokeWidth={2} name="Balance" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      )}
    </div>
  )
}
